import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Settings, Save, Wallet, Loader2 } from "lucide-react";
import { getWeb3Config, updateWeb3Config, getLifiFees } from "../../api/gidexApi";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

const GidexFeesPage = () => {
  const [activeTab, setActiveTab] = useState("config");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [feeBalances, setFeeBalances] = useState(null);

  useEffect(() => {
    if (activeTab === "config") {
      fetchConfig();
    } else if (activeTab === "balances") {
      fetchFeeBalances();
    }
  }, [activeTab]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getWeb3Config();
      const data = res.data?.configs || res.data?.data?.configs || [];
      const lifiConfig = data.filter((c) => c.provider === "lifi");
      setConfigs(lifiConfig);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch config");
    } finally {
      setLoading(false);
    }
  };

  const fetchFeeBalances = async () => {
    setLoading(true);
    try {
      const res = await getLifiFees();
      setFeeBalances(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch fee balances");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (configItem) => {
    setSaving(true);
    try {
      await updateWeb3Config(configItem.provider, {
        feeValue: configItem.feeValue,
        feeRecipientWallet: configItem.feeRecipientWallet,
        integratorId: configItem.integratorId,
        feePercent: configItem.feePercent,
      });
      toast.success(`${configItem.provider} config updated successfully`);
      fetchConfig();
    } catch (err) {
      const errorData = err.response?.data;
      const errorMsg = errorData?.error || errorData?.message || "Failed to update config";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (index, key, value) => {
    setConfigs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const getChainName = (chainId) => {
    const chains = {
      1: "Ethereum",
      56: "BNB Chain",
      137: "Polygon",
      42161: "Arbitrum",
      10: "Optimism",
      43114: "Avalanche",
      250: "Fantom",
    };
    return chains[chainId] || `Chain ${chainId}`;
  };

  const thCls = "border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left";
  const tdCls = "border-b border-border/40 px-5 py-3.5 text-sm text-foreground";

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Services
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Gidex Fees <span className="text-gradient-ginox">Management</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Configure fee settings and view collected fee balances.
      </p>

      {/* Tabs */}
      <div className="flex overflow-x-auto mb-6 border-b border-border/70 gap-1">
        <button
          onClick={() => setActiveTab("config")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition whitespace-nowrap -mb-px ${
            activeTab === "config"
              ? "border-ginox-green text-ginox-green"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Settings className="w-3.5 h-3.5" /> Fees Config
        </button>
        <button
          onClick={() => setActiveTab("balances")}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition whitespace-nowrap -mb-px ${
            activeTab === "balances"
              ? "border-ginox-green text-ginox-green"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Wallet className="w-3.5 h-3.5" /> Collected Fees
        </button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-muted-foreground py-12">Loading...</div>
      ) : (
        <>
          {/* Config Tab */}
          {activeTab === "config" && (
            <div className="space-y-5">
              {configs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No config data available</p>
              ) : (
                configs.map((configItem, index) => (
                  <div key={configItem.id} className="rounded-lg border border-border/70 bg-card p-5">
                    <p className="text-sm font-semibold text-foreground mb-4 capitalize">
                      {configItem.provider} Configuration
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Provider
                        </label>
                        <input
                          type="text"
                          value={configItem.provider || ""}
                          disabled
                          className={inputCls + " opacity-50 cursor-not-allowed"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Fee Percent
                        </label>
                        <input
                          type="number"
                          step="any"
                          value={configItem.feePercent ?? ""}
                          onChange={(e) => handleConfigChange(index, "feePercent", Number(e.target.value))}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Integrator ID
                        </label>
                        <input
                          type="text"
                          value={configItem.integratorId || ""}
                          onChange={(e) => handleConfigChange(index, "integratorId", e.target.value)}
                          className={inputCls}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                          Fee Recipient Wallet
                        </label>
                        <input
                          type="text"
                          value={configItem.feeRecipientWallet || ""}
                          onChange={(e) => handleConfigChange(index, "feeRecipientWallet", e.target.value)}
                          className={inputCls + " font-mono"}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleSaveConfig(configItem)}
                      disabled={saving}
                      className="mt-4 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save {configItem.provider}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Fee Balances Tab */}
          {activeTab === "balances" && (
            <div className="border-t border-white/[0.08] pt-5">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-5">
                <p className="text-sm font-semibold text-foreground">LiFi Collected Fees</p>
                {feeBalances?.integratorId && (
                  <span className="text-xs text-muted-foreground font-mono">
                    Integrator: {feeBalances.integratorId}
                  </span>
                )}
              </div>

              {!feeBalances?.feeBalances?.length ? (
                <p className="text-sm text-muted-foreground">No fee balances available</p>
              ) : (
                <div className="space-y-5">
                  {feeBalances.feeBalances.map((chainBalance) => (
                    <div key={chainBalance.chainId} className="rounded-lg border border-border/70 p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-3">
                        {getChainName(chainBalance.chainId)}{" "}
                        <span className="font-mono">(ID: {chainBalance.chainId})</span>
                      </p>

                      {/* Mobile Card View */}
                      <div className="block md:hidden space-y-3">
                        {chainBalance.tokenBalances.map((tokenBal, idx) => (
                          <div key={idx} className="rounded-md border border-border/70 bg-white/[0.02] p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                {tokenBal.token.logoURI && (
                                  <img
                                    src={tokenBal.token.logoURI}
                                    alt={tokenBal.token.symbol}
                                    className="w-6 h-6 rounded-full mr-2"
                                  />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-foreground">{tokenBal.token.symbol}</p>
                                  <p className="text-xs text-muted-foreground">{tokenBal.token.name}</p>
                                </div>
                              </div>
                              <span className="text-sm font-mono text-emerald-400">${tokenBal.amountUsd}</span>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {(Number(tokenBal.amount) / Math.pow(10, tokenBal.token.decimals)).toFixed(6)}{" "}
                              {tokenBal.token.symbol}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full border-separate border-spacing-0">
                          <thead>
                            <tr>
                              <th className={thCls}>Token</th>
                              <th className={thCls}>Symbol</th>
                              <th className={thCls + " text-right"}>Amount</th>
                              <th className={thCls + " text-right"}>USD Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chainBalance.tokenBalances.map((tokenBal, idx) => (
                              <tr key={idx} className="group hover:bg-white/[0.02] transition-colors">
                                <td className={tdCls}>
                                  <div className="flex items-center">
                                    {tokenBal.token.logoURI && (
                                      <img
                                        src={tokenBal.token.logoURI}
                                        alt={tokenBal.token.symbol}
                                        className="w-5 h-5 rounded-full mr-2"
                                      />
                                    )}
                                    {tokenBal.token.name}
                                  </div>
                                </td>
                                <td className={`${tdCls} font-mono`}>{tokenBal.token.symbol}</td>
                                <td className={`${tdCls} font-mono text-right`}>
                                  {(Number(tokenBal.amount) / Math.pow(10, tokenBal.token.decimals)).toFixed(6)}
                                </td>
                                <td className={`${tdCls} font-mono text-right text-emerald-400`}>
                                  ${tokenBal.amountUsd}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GidexFeesPage;
